import { useCallback, useEffect, useState } from "react";
import { CalendarPlus, History, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { adminApi, errMsg } from "@/lib/api";

export const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function StatusBadge({ status }) {
  return (
    <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${status === "Active" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700"}`}>{status}</span>
  );
}

function Detail({ id, onClose, onChanged }) {
  const [acc, setAcc] = useState(null);
  const [days, setDays] = useState("");
  const [date, setDate] = useState("");
  useEffect(() => {
    if (id) adminApi.get(`/accounts/${id}`).then((r) => setAcc(r.data));
    else setAcc(null);
  }, [id]);
  const setExpiry = async (body) => {
    try {
      const { data } = await adminApi.post(`/accounts/${id}/set-expiry`, body);
      setAcc(data);
      onChanged();
      toast.success("Expiry updated");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92dvh] max-w-lg overflow-y-auto" data-testid="admin-account-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{acc?.name || "…"}</DialogTitle>
          <DialogDescription>{acc ? `${acc.owner_email} · ${acc.product_count} products · ${acc.sale_count} sales` : ""}</DialogDescription>
        </DialogHeader>
        {acc && (
          <div className="space-y-5">
            <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3 text-sm">
              <span>Expires <b>{fmtDT(acc.demo_expires_at)}</b></span>
              <span className="flex items-center gap-2"><span className="money font-bold" data-testid="admin-detail-days-left">{acc.days_left}d</span><StatusBadge status={acc.status} /></span>
            </div>
            <div>
              <div className="eyebrow">Extension history</div>
              <ul className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-200" data-testid="admin-history-list">
                {acc.history.length === 0 && <li className="p-3 text-sm text-slate-400">No extensions yet.</li>}
                {acc.history.map((h) => (
                  <li key={h.id} className="p-3 text-sm" data-testid={`admin-history-${h.id}`}>
                    <div className="font-semibold text-slate-900">{h.kind === "extend_15" ? "Extended 15 days" : "Expiry set (test helper)"}</div>
                    <div className="text-xs text-slate-500">{fmtDT(h.at)} by {h.admin_email}</div>
                    <div className="font-mono text-[11px] text-slate-500">{fmtDT(h.previous_expires_at)} → {fmtDT(h.new_expires_at)}</div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/50 p-3">
              <div className="eyebrow !text-amber-800">Testing helper · set expiry</div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Input type="number" step="any" placeholder="days from now (e.g. 2, -1)" className="h-10 w-48" value={days} onChange={(e) => setDays(e.target.value)} data-testid="admin-set-days-input" />
                <button onClick={() => days !== "" && setExpiry({ days_from_now: Number(days) })} data-testid="admin-set-days-button" className="h-10 rounded-lg bg-slate-900 px-3 text-sm font-semibold text-white">Set</button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Input type="datetime-local" className="h-10 w-48" value={date} onChange={(e) => setDate(e.target.value)} data-testid="admin-set-date-input" />
                <button onClick={() => date && setExpiry({ expires_at: new Date(date).toISOString() })} data-testid="admin-set-date-button" className="h-10 rounded-lg bg-slate-900 px-3 text-sm font-semibold text-white">Set date</button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminAccounts() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => adminApi.get("/accounts", { params: { q, status } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), [q, status]);
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const extend = async (r) => {
    setBusy(r.id);
    try {
      const { data } = await adminApi.post(`/accounts/${r.id}/extend`);
      toast.success(`${r.name}: ${data.days_left} days left`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="admin-accounts">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Accounts</h1>
          <p className="text-sm text-slate-500" data-testid="admin-accounts-count">{rows ? `${rows.length} businesses` : "Loading…"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Business name or email" data-testid="admin-accounts-search" className="h-11 w-64 rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-slate-900" />
          </div>
          <div className="flex gap-1 rounded-xl bg-slate-200/70 p-1">
            {["all", "active", "expired"].map((s) => (
              <button key={s} onClick={() => setStatus(s)} data-testid={`admin-filter-${s}`} className={`h-9 rounded-lg px-3 text-sm font-semibold capitalize ${status === s ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>{s}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full min-w-[980px] text-left text-sm" data-testid="admin-accounts-table">
          <thead className="border-b border-slate-200 bg-slate-50 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <tr>{["Business", "Type", "Country", "Owner email", "Signed up", "Demo expiry", "Days left", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows?.map((r) => (
              <tr key={r.id} data-testid={`admin-account-row-${r.id}`}>
                <td className="px-4 py-3 font-semibold text-slate-900">{r.name}</td>
                <td className="px-4 py-3 text-slate-600">{r.business_type_label}</td>
                <td className="px-4 py-3 text-slate-600">{r.country_name}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-600">{r.owner_email}</td>
                <td className="px-4 py-3 text-slate-600">{fmtDT(r.created_at)}</td>
                <td className="px-4 py-3 text-slate-600">{fmtDT(r.demo_expires_at)}</td>
                <td className="money px-4 py-3 font-bold" data-testid={`admin-days-left-${r.id}`}>{r.days_left}</td>
                <td className="px-4 py-3" data-testid={`admin-status-${r.id}`}><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    <button onClick={() => setDetail(r.id)} data-testid={`admin-history-button-${r.id}`} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-600 hover:border-slate-900" aria-label="Details"><History className="h-4 w-4" /></button>
                    <button onClick={() => extend(r)} disabled={busy === r.id} data-testid={`admin-extend-button-${r.id}`} className="flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg bg-amber-500 px-3 text-xs font-bold text-slate-950 hover:bg-amber-400 disabled:opacity-50">
                      <CalendarPlus className="h-3.5 w-3.5" /> Extend 15 days
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows?.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-slate-400" data-testid="admin-accounts-empty">No accounts match.</td></tr>}
          </tbody>
        </table>
      </div>
      <Detail id={detail} onClose={() => setDetail(null)} onChanged={load} />
    </div>
  );
}
